import React from 'react';

import PageLayout from '@/components/layout/PageLayout';
import { PermissionGuard } from '@/components/layout';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons';
import { IconScout } from '@/components/ui/images/IconScout';
import Table from '@/components/ui/tables/Table';
import { useTranslation } from '@/i18n';
import { useFractionalLeaveList } from '@/hooks/fractionalLeave/useFractionalLeaveList';
import type { FractionalLeaveStatusFilter } from '@/api/domains/fractionalLeave/fractionalLeave.types';


export const FractionalLeaveList: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onSortChange,
    statusFilter,
    onStatusFilterChange,
    statusFilterOptions,
    isLoading,
    isExporting,
    openCreatePanel,
    onExport,
  } = useFractionalLeaveList();

  return (
    <PageLayout isLoading={isLoading}>
      <div className="flex flex-col gap-6" data-testid="fractional-leave-list-page">
        {/* Create button */}
        <div>
          <PermissionGuard action="create" fallback="hidden">
            <PrimaryButton
              iconLeft={<IconScout name="plus" />}
              onClick={openCreatePanel}
            >
              {translate('fractionalLeave:actions.create')}
            </PrimaryButton>
          </PermissionGuard>
        </div>

        {/* Filters bar */}
        <div className="flex flex-wrap gap-4 justify-between items-start">
          <FilterDropdown
            extraFilters={
              <div className="flex flex-col gap-3 pt-2">
                <Selector
                  label={translate('fractionalLeave:filters.status')}
                  options={statusFilterOptions}
                  value={statusFilter}
                  onChange={(value) => {
                    if (typeof value === 'string') {
                      onStatusFilterChange(value as FractionalLeaveStatusFilter);
                    }
                  }}
                />
              </div>
            }
          />
          <ExportDropdown
            onExport={onExport}
            placement="right"
            isLoading={isExporting}
            availableFormats={['EXCEL']}
          />
        </div>

        {/* Table */}
        <Table
          columns={columns}
          data={rows}
          totalRows={total}
          isLoading={isLoading}
          searchable
          onSearch={onSearch}
          serverSideSort
          onSortChange={onSortChange}
          actions={actions}
          pagination={{
            page,
            pageSize,
            total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          data-testid="fractional-leave-table"
        />
      </div>
    </PageLayout>
  );
};

export default FractionalLeaveList;
